import React from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  HStack,
  Progress,
  Text,
  Button,
  Box,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  Badge,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react'
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiXCircle,
  FiSkipForward,
  FiLoader,
} from 'react-icons/fi'
import { useTranslation } from 'react-i18next'

export type ProgressStatus =
  | 'idle'
  | 'processing'
  | 'completed'
  | 'partial'
  | 'failed'

export interface ProgressResults {
  queued: number
  alreadyQueued?: number
  failed: number
  skipped: number
}

const normalizeNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
  }
  return fallback
}

export const normalizeProgressResults = (
  results: ProgressResults
): Required<ProgressResults> => ({
  queued: normalizeNumber(results.queued),
  alreadyQueued: normalizeNumber(results.alreadyQueued),
  failed: normalizeNumber(results.failed),
  skipped: normalizeNumber(results.skipped),
})

export const getTriggerProgressStatus = (failed: number): ProgressStatus =>
  normalizeNumber(failed) > 0 ? 'partial' : 'completed'

export const getProgressStatusMessageKey = (status: ProgressStatus): string => {
  switch (status) {
    case 'processing':
      return 'progress.processing'
    case 'completed':
      return 'progress.completed'
    case 'partial':
      return 'progress.partial'
    case 'failed':
      return 'progress.failed'
    default:
      return ''
  }
}

export interface ProgressModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  total: number
  current: number
  status: ProgressStatus
  message?: string
  results?: ProgressResults
  onCancel?: () => void
  canCancel?: boolean
}

export const ProgressModal: React.FC<ProgressModalProps> = ({
  isOpen,
  onClose,
  title,
  total,
  current,
  status,
  message,
  results,
  onCancel,
  canCancel = false,
}) => {
  const { t } = useTranslation()
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const statBg = useColorModeValue('gray.50', 'gray.900')

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return (
          <Icon
            as={FiLoader}
            className="animate-spin"
            color="blue.500"
            boxSize={5}
          />
        )
      case 'completed':
        return <Icon as={FiCheckCircle} color="blue.500" boxSize={5} />
      case 'partial':
        return <Icon as={FiAlertTriangle} color="orange.500" boxSize={5} />
      case 'failed':
        return <Icon as={FiXCircle} color="red.500" boxSize={5} />
      default:
        return null
    }
  }

  const getStatusColor = () => {
    switch (status) {
      case 'processing':
        return 'blue'
      case 'completed':
        return 'blue'
      case 'partial':
        return 'orange'
      case 'failed':
        return 'red'
      default:
        return 'gray'
    }
  }

  const normalizedTotal = normalizeNumber(total)
  const normalizedCurrent = normalizeNumber(current)

  const progressPercentage =
    normalizedTotal > 0
      ? Math.min(100, Math.max(0, (normalizedCurrent / normalizedTotal) * 100))
      : 0
  const isIndeterminate =
    status === 'processing' && normalizedCurrent === 0 && normalizedTotal === 0

  const normalizedResults = results ? normalizeProgressResults(results) : null
  const statusMessageKey = getProgressStatusMessageKey(status)

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={status !== 'processing'}
      closeOnEsc={status !== 'processing'}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent bg={bgColor}>
        <ModalHeader>
          <HStack spacing={3}>
            {getStatusIcon()}
            <Text>{title}</Text>
          </HStack>
        </ModalHeader>
        {status !== 'processing' && <ModalCloseButton />}

        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Progress Bar */}
            <Box>
              <HStack justify="space-between" mb={2}>
                <Text fontSize="sm" color="gray.600">
                  {status === 'processing'
                    ? t('progress.processing', {
                        current: normalizedCurrent,
                        total: normalizedTotal,
                      })
                    : statusMessageKey
                      ? t(statusMessageKey)
                      : ''}
                </Text>
                {!isIndeterminate && normalizedTotal > 0 && (
                  <Text fontSize="sm" color="gray.600">
                    {progressPercentage.toFixed(0)}%
                  </Text>
                )}
              </HStack>
              <Progress
                value={progressPercentage}
                size="sm"
                colorScheme={getStatusColor()}
                isIndeterminate={isIndeterminate}
                hasStripe={status === 'processing'}
                isAnimated={status === 'processing'}
              />
            </Box>

            {/* Message */}
            {message && (
              <Box
                p={3}
                borderRadius="md"
                borderWidth={1}
                borderColor={borderColor}
                bg={statBg}
              >
                <Text fontSize="sm">{message}</Text>
              </Box>
            )}

            {/* Results Summary */}
            {normalizedResults && status !== 'processing' && (
              <Box>
                <Text fontWeight="semibold" mb={3}>
                  {t('progress.results')}
                </Text>
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3}>
                  <Box
                    p={3}
                    borderRadius="md"
                    borderWidth={1}
                    borderColor={borderColor}
                    bg={statBg}
                  >
                    <Stat size="sm">
                      <StatLabel>
                        <HStack spacing={1}>
                          <Icon as={FiClock} color="blue.500" />
                          <Text>{t('progress.queued')}</Text>
                        </HStack>
                      </StatLabel>
                      <StatNumber color="blue.600">
                        {normalizedResults.queued}
                      </StatNumber>
                    </Stat>
                  </Box>
                  <Box
                    p={3}
                    borderRadius="md"
                    borderWidth={1}
                    borderColor={borderColor}
                    bg={statBg}
                  >
                    <Stat size="sm">
                      <StatLabel>
                        <HStack spacing={1}>
                          <Icon as={FiCheckCircle} color="cyan.500" />
                          <Text>{t('progress.alreadyQueued')}</Text>
                        </HStack>
                      </StatLabel>
                      <StatNumber color="cyan.600">
                        {normalizedResults.alreadyQueued}
                      </StatNumber>
                    </Stat>
                  </Box>
                  <Box
                    p={3}
                    borderRadius="md"
                    borderWidth={1}
                    borderColor={borderColor}
                    bg={statBg}
                  >
                    <Stat size="sm">
                      <StatLabel>
                        <HStack spacing={1}>
                          <Icon as={FiXCircle} color="red.500" />
                          <Text>{t('progress.failedItems')}</Text>
                        </HStack>
                      </StatLabel>
                      <StatNumber color="red.600">
                        {normalizedResults.failed}
                      </StatNumber>
                    </Stat>
                  </Box>
                  <Box
                    p={3}
                    borderRadius="md"
                    borderWidth={1}
                    borderColor={borderColor}
                    bg={statBg}
                  >
                    <Stat size="sm">
                      <StatLabel>
                        <HStack spacing={1}>
                          <Icon as={FiSkipForward} color="yellow.500" />
                          <Text>{t('progress.skipped')}</Text>
                        </HStack>
                      </StatLabel>
                      <StatNumber color="yellow.600">
                        {normalizedResults.skipped}
                      </StatNumber>
                    </Stat>
                  </Box>
                </SimpleGrid>
              </Box>
            )}

            {/* Status Badge */}
            <HStack justify="center">
              <Badge
                colorScheme={getStatusColor()}
                fontSize="md"
                px={3}
                py={1}
                borderRadius="md"
              >
                {status === 'processing'
                  ? t('progress.statusProcessing')
                  : status === 'completed'
                    ? t('progress.statusCompleted')
                    : status === 'partial'
                      ? t('progress.statusPartial')
                      : status === 'failed'
                        ? t('progress.statusFailed')
                        : t('progress.statusIdle')}
              </Badge>
            </HStack>
          </VStack>
        </ModalBody>

        <ModalFooter>
          {status === 'processing' && canCancel && onCancel && (
            <Button variant="ghost" colorScheme="red" onClick={onCancel}>
              {t('progress.cancel')}
            </Button>
          )}
          {status !== 'processing' && (
            <Button colorScheme="blue" onClick={onClose}>
              {t('common.close')}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
